import { useDispatch } from 'react-redux';

// Design System
import { Badge } from 'jl-design-system/components/badge/Badge';
import RadioButton from 'jl-design-system/components/radio-button/RadioButton';
import { TertiaryButton } from 'jl-design-system/elements/button/Button';

// Types
import { AppDispatch } from 'types/RootState.types';
import { DeliveryAddressCardProps, Address } from './DeliveryAddressCard.types';

// Config
import { selectAddress } from '@redux/actions/addressBook/addressBookActions';
import { DELIVERY_ADDRESS_FORM_ID } from '@utils/form/configs/deliveryAddress';

// Components
import SaveAsDefaultForm from '../save-as-default-form/SaveAsDefaultForm';
import BodyText from '@components/body-text';
import Container from '@components/container';

// Styles
import styles from './delivery-address-card.scss';

const DeliveryAddressCard = ({
  address = {},
  isSelectedAddress,
  addressNotAvailable,
  addressee = {},
  addressId = '',
  editAddressHandler = () => {},
  handleChangeAddressClick,
  invalidAddress = false,
  isChoosingAddress,
  isContactAddress,
  isPreferredAddress,
  phoneNumber,
  removeAddressHandler = () => {},
  toggleAddNewAddressForm = () => {},
}: DeliveryAddressCardProps) => {
  const dispatch: AppDispatch = useDispatch();

  const keyOrder: (keyof Address)[] = [
    'addressLine1',
    'addressLine2',
    'addressLine3',
    'townOrCity',
    'countyStateOrProvince',
    'postcode',
  ];

  const handleOnChange = () => {
    toggleAddNewAddressForm();
    dispatch(
      selectAddress({
        formId: DELIVERY_ADDRESS_FORM_ID,
        handleModalClose: undefined,
        selectedAddressId: addressId,
        unsavedAddress: undefined,
      }),
    );
  };

  const notAvailable = addressNotAvailable?.notAvailableForDelivery;
  const notAvailableText = addressNotAvailable?.unavailabilityErrorMessage;

  const handleEditClick = () => editAddressHandler(addressId, invalidAddress);

  const startsWithNumber = (addressLine: string) => addressLine.match(/^[0-9]+$/);

  const formattedAddress = keyOrder
    .map((key) => {
      if (key === 'addressLine2' && startsWithNumber(address.addressLine1 || '')) {
        return null;
      }
      if (key === 'addressLine1' && startsWithNumber(address.addressLine1 || '')) {
        return `${address.addressLine1 || ''} ${address.addressLine2 || ''}`.trim();
      }
      if (key === 'postcode') {
        return `${address[key] || ''}`;
      }
      return address[key];
    })
    .filter((value) => value)
    .join(', ');

  const addresseeTitle = addressee?.title ? `${addressee.title}` : '';
  const formattedAddressee = `${addresseeTitle} ${addressee.firstName} ${addressee.lastName}`;

  return isChoosingAddress ? (
    <RadioButton
      checked={isSelectedAddress}
      className={styles.carouselCard}
      disabled={invalidAddress || notAvailable}
      isButton
      isControlled
      label={formattedAddressee}
      labelClassName={styles.heading}
      name="address-book-rad"
      onChange={handleOnChange}
      value={formattedAddressee}
    >
      <Container flex={{ flexDirection: 'col' }}>
        <BodyText className={styles.subtext}>{formattedAddress}</BodyText>
        <BodyText className={styles.subtext}>{phoneNumber}</BodyText>
        {(isPreferredAddress || isContactAddress) && (
          <div className={styles.badgeContainer}>
            {isPreferredAddress && (
              <div className={styles.addressBadge}>
                <Badge badgeType="--neutral_15" label="Preferred Address" />
              </div>
            )}
            {isContactAddress && (
              <div className={styles.addressBadge}>
                <Badge badgeType="--neutral_15" label="Contact Address" />
              </div>
            )}
          </div>
        )}
      </Container>
      {notAvailable && (
        <Container marginTop="1" testId="unavailable-address-actions-container">
          {notAvailableText}
        </Container>
      )}
      <div className={styles.carouselCTA}>
        {!notAvailable && (
          <TertiaryButton onClick={handleEditClick} small variant="tonal-dark">
            Edit
          </TertiaryButton>
        )}
        <TertiaryButton
          data-testid="address-book-item-delete-address"
          onClick={removeAddressHandler}
          small
          variant="tonal-dark"
        >
          Delete
        </TertiaryButton>
      </div>
    </RadioButton>
  ) : (
    <Container border="grey-15" className={styles.container} marginTop="1" padding="2">
      <Container flex={{ flexDirection: 'col' }}>
        <Container flex={{ flexDirection: 'col' }}>
          <BodyText>{formattedAddressee}</BodyText>
          <BodyText>{formattedAddress}</BodyText>
          <BodyText>{phoneNumber}</BodyText>
          <SaveAsDefaultForm
            saveAsDefaultObject={{ id: addressId, defaultShipping: isPreferredAddress }}
            type="delivery"
          />
        </Container>
        <div className={styles.changeAddressButton}>
          <TertiaryButton
            data-testid="delivery-address-change-button"
            onClick={handleChangeAddressClick}
            small
            variant="tonal-dark"
          >
            Change address
          </TertiaryButton>
        </div>
      </Container>
    </Container>
  );
};

export default DeliveryAddressCard;
